import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import { z } from "../zod.js";
import {
  ChannelCreateRequest,
  ChannelDeleteResponse,
  ContactChannel,
  ContactCreateRequest,
  ContactUpdateRequest,
  Customer,
  Customer360Response,
  CustomerContact,
  CustomerCreateRequest,
  CustomerDistributorProduct,
  CustomerListResponse,
  CustomerPriceOverride,
  CustomerPriceOverrideCreateRequest,
  CustomerPriceOverrideDecideRequest,
  CustomerPriceOverrideListResponse,
  CustomerPriceOverrideRevokeRequest,
  CustomerReliabilityHistoryResponse,
  CustomersOrder,
  CustomerTier,
  CustomerUpdateRequest,
  DistributorProductAddRequest,
  DistributorProductRemoveResponse,
  MarginGuard,
  MarginGuardCreateRequest,
  MarginGuardListResponse,
  MarginGuardScope,
  MarginGuardUpdateRequest,
  OffcutDiscountClass,
  OffcutDiscountClassCreateRequest,
  OffcutDiscountClassListResponse,
  OffcutDiscountClassUpdateRequest,
  OverrideStatus,
  PriceList,
  PriceListCreateRequest,
  PriceListDetailResponse,
  PriceListItemsPutRequest,
  PriceListItemsPutResponse,
  PriceListListResponse,
  PriceListStatus,
  PricingResolveRequest,
  PricingResolveResponse,
  ResolveIdentityRequest,
  ResolveIdentityResponse,
  SalesActivity,
  SalesActivityCreateRequest,
  SalesActivityListQuery,
  SalesActivityListResponse,
  SalesActivityUpdateRequest,
  SalesCurrency,
  SalesSettings,
  SalesSettingsPutRequest,
} from "./schemas.js";

// Module 09 — sales HTTP surface (spec 09 §5), SLICE P3-A: SALES FOUNDATIONS —
// the customer book (create/list/360/patch/archive, contacts, channels,
// resolve-identity, distributor eligible list, reliability history), the tier
// price engine's configuration (price lists + items + activate/archive,
// off-cut discount classes, margin guards), the tenant sales settings, and
// the §3.2 pricing dry-run — PLUS, since slice P3-C-2 (2026-09-08, D-263),
// the §2.9 customer price override surface: create / decide / list / revoke,
// mounted beside the pricing dry-run they feed — PLUS, since P3-E PR-E2
// (D-292; migration 0067), the manager's TASKS (activities step 1): list,
// create and complete, mounted at the end of this file — PLUS, since P3-E2
// PR E2-1 (D-313; migration 0073), the task PATCH and withdraw, and the
// customer book's next step (`needs_next_step`).
//
// WHAT IS ABSENT, AND WHY THAT IS THE POINT. §5's quote/order/shipment/
// notification/stats rows land with their owning slices on the ROADMAP's
// canonical P3 lettering (D-252; re-stated here 2026-09-02, D-258 — the
// "P3-B/P3-C/P3-D/P3-E" this header first said was the stale lettering):
// quotes P3-C (the quote core shipped as P3-C-1 — `quote-register.ts`),
// orders/shipments P3-D, notifications/stats P3-E. A path with no slice yet
// is absent from openapi/v1.json entirely — a 404 on a route that does not
// exist is honest; a 501 stub is not (D-026/D-033, the statement every
// module's first slice wrote). The §2.9 override WRITE surface was absent on
// the same rule from P3-A through P3-C-1: the decide route's event
// (`sales.override_review_decided`) had no emitter until the route existed,
// and a route whose declared event nothing emits is the same defect pointed
// the other way. P3-C-2 closes that — the four routes, the event, and the
// `OVERRIDE_*` problem codes land TOGETHER (D-255 ruling 1's pairing rule),
// and the "WHAT STAYS ABSENT" ledger in `quote-register.ts` records the
// landing rather than the wait.
//
// STATED DEGRADATIONS THIS SURFACE CARRIES (the P3 block at the top of spec
// 09, each restated on its route):
//   * open AR reads 0 with `open_ar.available=false` (§3.5b — a typed fact,
//     never server prose, D-225);
//   * reliability is PROVISIONAL — §3.9's job emits nothing until module 11;
//     seeded demo scores carry `provisional: true` too;
//   * `cost_basis='moving_avg'` → 422 COST_BASIS_UNAVAILABLE until module 11;
//   * `vat_mode` creation defaults by tier per D-234 (data, never a code path);
//   * `scope='family'` margin guards → 422 FAMILY_SCOPE_UNAVAILABLE — module
//     01's shipped schema has no product_families table (P3-A decision).
//
// PERMISSIONS — one code per route (the register's invariant), cell
// narrowings service-side (D-040 two-layer); the cell-by-cell transcription
// lives in 0050_sales_foundations.sql. The §7 document codes register with
// the slices that mount their routes: `sales.quote.write`, `sales.quote.send`
// and `sales.order.cancel` landed with P3-C-1 (0055); `sales.override.write`
// lands HERE with P3-C-2 (D-263: ONE code for create AND revoke — roles
// ga/fa/sales, revoke service-narrowed to fa/ga, a second code rejected
// because its only holders already hold the first); `sales.shipment.post`
// lands with P3-D's shipment rows. `sales.order.confirm` — which this header
// once counted as the fifth — is NOT a code: manual acceptance rides
// `sales.quote.send`, no route reads it, and spec 09 §7 struck it (D-258; the
// sales block's note in `../permissions.ts`).

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch" | "put" | "delete";
  path: string;
  operationId: string;
  summary: string;
  permission?: string;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
  description?: string;
}): void {
  const mutation = config.method !== "get";
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["sales"],
    summary: config.summary,
    description:
      (config.description ?? "") +
      (config.permission !== undefined ? ` Requires \`${config.permission}\`.` : ""),
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(mutation ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const jsonBody = (schema: z.ZodType) => ({
  body: { content: { "application/json": { schema } } },
});
const ok = (description: string, schema: z.ZodType) => ({
  200: { description, content: { "application/json": { schema } } },
});
const created = (description: string, schema: z.ZodType) => ({
  201: { description, content: { "application/json": { schema } } },
});

const IdParam = z.object({ id: z.guid() });

/** Every door that answers 422 `CUSTOMER_ARCHIVED` says this since P3-E2 PR
 * E2-2b (spec 09 §5's E2-2 row 22): the problem names the customer a merged
 * customer was merged into, built by `customerArchivedProblem(row)`. */
const MERGED_EXTENSION =
  "`CUSTOMER_ARCHIVED` carries `extensions.merged_into_customer_id` naming the winner when the customer was merged (spec 09 §5's E2-2 row 22)";

/** Cursor pagination (§5): default 50, max 200. */
const cursorQuery = {
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
};

/** Two-value string enum, never z.coerce.boolean() — module 05's published
 * mistake, restated: `?credit_hold=no` would coerce to true. */
const BoolQuery = z.enum(["true", "false"]).optional();

// ===========================================================================
// customers (spec 09 §2.1–§2.2, §3.1; §5 rows 1–5)
// ===========================================================================

route({
  method: "post",
  path: "/v1/customers",
  operationId: "createSalesCustomer",
  summary: "Create a customer (with inline contacts + channels)",
  permission: "sales.manage_customers",
  description:
    "Spec 09 §3.1 step 1 — `customer_no` is assigned `CUS-{seq5}` from the per-tenant series; `country`/`default_currency` default to the tenant's values when omitted. The creating sales user becomes the §2.2 owner assignment (admins may pass `owner_user_id` via PATCH after). Emits `sales.customer_created`. §9.13's currency guard is a WARNING: when no price list exists for the chosen currency the 201 carries the response header `X-Sales-Warning: NO_PRICE_LIST_FOR_CURRENCY`, a typed code the client localizes, and creation is never blocked by it. An idempotent replay does not repeat the header. Since P3-E2 PR E2-2a1 (D-323 ruling 2) `notes` appends a typed note and pins it, audited `sales_customer_note.appended` beside `sales.customer_created`, and an empty or whitespace `notes` appends nothing; the answer's `notes` is the pinned note's body. The answer is stored for replay without `notes` and `pinned_note` (spec 09 §2.26), so a replay carries no note text and the client reads the customer again. Since P3-E2 PR E2-2c (spec 09 §3.13, §5's E2-2 row 18, D-324 rulings 2 and 3) `X-Sales-Warning` is a comma-separated list of typed codes in a fixed order, `NO_PRICE_LIST_FOR_CURRENCY` first and then `POSSIBLE_DUPLICATE` (for example `X-Sales-Warning: NO_PRICE_LIST_FOR_CURRENCY,POSSIBLE_DUPLICATE`), absent when neither applies. `POSSIBLE_DUPLICATE` means the duplicate check (`POST /v1/customers/check-duplicates`), run on the new customer's names and tax id, named a customer other than the one just created that the caller's customer door answers, and the customer is created all the same; a match on a customer the caller cannot open sets no warning (§7 narrowing 20). Also since E2-2c (spec 09 §3.15, D-332 ruling 1, which answered §12 question 14) the create applies the PATCH's credit rule: a caller with no lineage in the credit cell (group_admin, facility_admin, finance) that sends `credit_limit_minor`, `credit_terms_days` or `credit_hold` is refused 403 and nothing is written, so a representative's new customer starts with no limit and the order credit gate (§3.5b) reads only a limit a credit role set.",
  request: jsonBody(CustomerCreateRequest),
  responses: {
    ...created("The created customer", Customer),
    403: problemResponse(
      "PERMISSION_DENIED: with no grant named, for a caller outside the customer write lineages; then, since P3-E2 PR E2-2c, for a caller with no credit lineage (group_admin, facility_admin, finance) whose body carries `credit_limit_minor`, `credit_terms_days` or `credit_hold` (spec 09 §3.15, D-332 ruling 1)",
    ),
    409: problemResponse(
      "IDENTITY_TAKEN — an inline channel identity already resolves to another customer. The extension `owning_customer_id` names it only for a caller holding `messaging.address.read`; any other writer is told the address is taken and never whose (spec 09 §7, §9.12, D-307)",
    ),
    422: problemResponse("IDENTITY_INVALID / OWNER_NOT_MEMBER"),
  },
});

route({
  method: "get",
  path: "/v1/customers",
  operationId: "listSalesCustomers",
  summary: "List/search customers (cursor page)",
  permission: "sales.read",
  description:
    "Spec 09 §4.1/§5 — `q` matches display_name (trigram), customer_no and tax_id for every reader, and phone/channel identities (`contact_channels.identity_value`) only for a caller holding `messaging.address.read`. For any other caller the identity leg is left out of the query, so no digit of an address the caller is served masked decides which customers come back (spec 09 §7, D-307). The sales role sees only assigned customers (`customer_assignments`, service-enforced on top of RLS). `order` is the D-242 typed sort surface (default `-created_at`, the server-applied declared default; `reliability_score` sorts NULLS LAST in both directions); the cursor encodes the order it was minted under, and a cursor presented under a DIFFERENT effective order answers 400 `invalid_cursor` — the client resets to page 1 on sort change, the server never trusts that it did. `credit_limit_minor` is omitted per §7 outside ga/fa/finance/sales-assigned. Since P3-E2 PR E2-1 each item carries its NEXT STEP (D-313 rulings 6 and 7): `next_activity`, the earliest open task on the customer by kind and date only, and `needs_next_step`, true for an active customer with live business (an open quote, order or deal) and no open task. Both are served to every reader and never withheld, and `needs_next_step=true` filters on the flag (it takes `true` only, the `shippable` shape; spec 16 §9.23 admits it to a saved view because nothing it reads is withheld). Since P3-E2 PR E2-2a1 (D-323 rulings 5 to 7) each item carries `tags` (every tag the customer carries, archived ones with their status) and `has_live_business` (the archive guard's predicate), both served to every reader, and `tag_id` keeps the customers carrying that tag, an archived tag included. A tag the caller's tenant does not hold matches no customer. `tag_id` is a REST parameter in E2-2a1's server wave and joins the `sales.customers` view grammar as `tag` in its web wave (D-323 ruling 6). Since P3-E2 PR E2-2b each item also carries the four tombstone fields the 360 carries (`merged_into_customer_id`, `merged_into_display_name`, `merged_into_openable`, `merged_at`), null on a customer that was not merged (spec 09 §5's E2-2 row 20).",
  request: {
    query: z.object({
      q: z.string().max(200).optional(),
      tier: CustomerTier.optional(),
      status: z.enum(["active", "archived"]).optional(),
      owner_user_id: z.guid().optional(),
      credit_hold: BoolQuery,
      needs_next_step: z.enum(["true"]).optional(),
      tag_id: z.guid().optional(),
      order: CustomersOrder.optional(),
      ...cursorQuery,
    }),
  },
  responses: ok("Customers, newest first", CustomerListResponse),
});

route({
  method: "get",
  path: "/v1/customers/{id}",
  operationId: "getSalesCustomer360",
  summary: "Customer 360 payload",
  permission: "sales.read",
  description:
    "Spec 09 §4.2/§5 — customer + contacts + channels + assignments + latest reliability snapshot + distributor products. `open_ar.available=false` until module 11 (§3.5b stated degradation); reliability formula inputs and `credit_limit_minor` are omitted per §7 for non-privileged roles; the snapshot's `provisional` flag is §3.9's typed fact (always true in P3, seeded scores included). Each channel's `identity_value` is served in clear only to a caller holding `messaging.address.read`; every other caller gets spec 17 §7's mask (`+7•••••1234` for phone and WhatsApp, `chat:•••482` for Telegram, `•••@domain` for email), with the key present (spec 09 §7, D-307). The office paints the channel list in the masked form for every reader; the wire keeps the stored value for a holder. Since P3-E2 PR E2-1 the customer carries its next step, `next_activity` and `needs_next_step`, served to every reader exactly as the list serves them (D-313 rulings 6 and 7). Since P3-E2 PR E2-2a1 (D-323 rulings 1, 2 and 5) the customer carries `tags` and `has_live_business` as the list does, and `pinned_note` beside it is the pinned `CustomerNote` or null. The customer's `notes` is the pinned note's body, or null while no note is pinned or the pinned note is redacted (spec 09 §2.26). Since P3-E2 PR E2-2b (D-324 ruling 15, spec 09 §5's E2-2 row 20) a merged customer still answers, as the tombstone: the customer carries `merged_into_customer_id`, `merged_into_display_name` (the winner's current name), `merged_into_openable` (whether the caller's own customer door answers the winner) and `merged_at`, each null on a customer that was not merged.",
  request: { params: IdParam },
  responses: {
    ...ok("The 360 payload", Customer360Response),
    404: problemResponse("CUSTOMER_NOT_FOUND (cross-tenant ids are invisible)"),
  },
});

route({
  method: "patch",
  path: "/v1/customers/{id}",
  operationId: "updateSalesCustomer",
  summary: "Update profile / credit / tier",
  permission: "sales.manage_customers",
  description:
    'Spec 09 §5 — field-group narrowings are SERVICE-enforced (D-040 two-layer): sales edits profile fields on assigned customers only; credit limit/terms/hold require the §7 credit cell (ga/fa/finance — finance may touch ONLY credit fields); tier changes require `sales.tier.manage` and follow §3.1 (a change TO distributor prices as b2b until the eligible list has rows; emits `sales.customer_tier_changed`). Credit-field changes emit `sales.customer_credit_changed`. `owner_user_id` reassigns the §2.2 owner (admins). Since P3-E2 PR E2-2a1 (D-323 ruling 2) `notes` is the alias of the pinned note: a value appends a typed note and pins it (audited `sales_customer_note.appended`), and `null`, `""` or a value that is empty after trim unpins the pinned note and keeps it (audited `sales_customer_note.unpinned`). Sending again the value the alias serves writes nothing, and that holds for `null` while the pinned note is redacted. A PATCH carrying `notes` alone leaves the customer row and its version as they were. The answer is stored for replay without `notes` and `pinned_note` (spec 09 §2.26).',
  request: { params: IdParam, ...jsonBody(CustomerUpdateRequest) },
  responses: {
    ...ok("The updated customer", Customer),
    404: problemResponse("CUSTOMER_NOT_FOUND"),
    422: problemResponse(`OWNER_NOT_MEMBER / CUSTOMER_ARCHIVED. ${MERGED_EXTENSION}`),
  },
});

route({
  method: "post",
  path: "/v1/customers/{id}/archive",
  operationId: "archiveSalesCustomer",
  summary: "Archive a customer",
  permission: "sales.manage_customers",
  description:
    "Spec 09 §3.1.4 — admins only (service-enforced). Allowed only with no open quotes, no open orders and, since P3-E2 PR E2-1, no open deal (D-312 ruling 13; a won or lost deal is finished with); archived customers stay resolvable by identity for inbound history but block new documents. The same live-business predicate raises the customer's `needs_next_step` flag, so the flag and this guard read one rule (D-313 ruling 7), and since P3-E2 PR E2-2a1 it is served on every list item as `has_live_business`, so bulk Archive marks a row it would refuse before the press. The answer is stored for replay without `notes` and `pinned_note` (spec 09 §2.26).",
  request: { params: IdParam },
  responses: {
    ...ok("The archived customer", Customer),
    404: problemResponse("CUSTOMER_NOT_FOUND"),
    409: problemResponse("CUSTOMER_HAS_OPEN_DOCS"),
  },
});

// ===========================================================================
// contacts + channels (spec 09 §2.3–§2.4; §5 rows 6–9, 13)
// ===========================================================================

route({
  method: "post",
  path: "/v1/customers/{id}/contacts",
  operationId: "createSalesContact",
  summary: "Add a contact",
  permission: "sales.manage_customers",
  description:
    "Spec 09 §5 — `is_primary: true` demotes the previous primary in the same transaction (partial unique, §2.3).",
  request: { params: IdParam, ...jsonBody(ContactCreateRequest) },
  responses: {
    ...created("The created contact", CustomerContact),
    404: problemResponse("CUSTOMER_NOT_FOUND"),
    422: problemResponse(`CUSTOMER_ARCHIVED. ${MERGED_EXTENSION}`),
  },
});

route({
  method: "patch",
  path: "/v1/contacts/{id}",
  operationId: "updateSalesContact",
  summary: "Edit or archive a contact",
  permission: "sales.manage_customers",
  description:
    "Spec 09 §5 — archiving (`status='archived'`) clears `is_primary` when the contact was primary and emits `sales.customer_contact_deleted` so module 17 clears channel routing (consumer declared honestly absent until 17 exists — the event is the contract).",
  request: { params: IdParam, ...jsonBody(ContactUpdateRequest) },
  responses: {
    ...ok("The updated contact", CustomerContact),
    404: problemResponse("CONTACT_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/contacts/{id}/channels",
  operationId: "createSalesContactChannel",
  summary: "Add a messaging identity",
  permission: "sales.manage_customers",
  description:
    "Spec 09 §2.4/§9.12 — `identity_value` is normalized service-side (E.164 / telegram chat id / lowercased email) before the UNIQUE(tenant, channel, identity) fence. A collision answers 409 IDENTITY_TAKEN, with the owning customer in the problem extensions (`owning_customer_id`) only for a caller holding `messaging.address.read`; any other writer is told the address is taken and never whose. Moving an identity is an explicit delete + re-add, audited, forward-only. The returned `identity_value` follows the customer 360's address rule: in clear for a caller holding `messaging.address.read`, masked for any other caller (spec 09 §7, D-307).",
  request: { params: IdParam, ...jsonBody(ChannelCreateRequest) },
  responses: {
    ...created("The created channel (normalized identity_value)", ContactChannel),
    404: problemResponse("CONTACT_NOT_FOUND"),
    409: problemResponse(
      "IDENTITY_TAKEN (extension `owning_customer_id`, only for a caller holding `messaging.address.read`; D-307)",
    ),
    422: problemResponse("IDENTITY_INVALID"),
  },
});

route({
  method: "delete",
  path: "/v1/channels/{id}",
  operationId: "deleteSalesContactChannel",
  summary: "Remove a messaging identity",
  permission: "sales.manage_customers",
  description:
    "Spec 09 §5 — admins only (service-enforced narrowing; §5's role cell is facility_admin). A hard delete: an identity is a routing fact, not a document; the platform audit chain records the removal (the channel's id, kind, contact and customer) and never the removed address (spec 09 §7, D-307).",
  request: { params: IdParam },
  responses: {
    ...ok("Removal confirmation", ChannelDeleteResponse),
    404: problemResponse("CHANNEL_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/customers/resolve-identity",
  operationId: "resolveSalesIdentity",
  summary: "Identity → customer (module 17 + agent hop)",
  permission: "sales.read",
  description:
    "Spec 09 §5 — the one-hop identity→customer resolution `contact_channels` is denormalized for. §5's role cell is service-to-service + facility_admin; the service narrows accordingly (sales-role calls resolve only identities of assigned customers). Row-or-nothing (§9.15): a miss is 404 IDENTITY_NOT_FOUND with no tenant-shaped detail. A caller without `messaging.address.read` is refused 403 PERMISSION_DENIED naming that grant before the value is normalized or looked up, whether or not it matches, because a caller served addresses masked can only send a guess (spec 09 §7 narrowing (14), D-307). POST, not GET: the identity is an argument, not a resource path, and must never land in access logs. Since P3-E2 PR E2-2b (spec 09 §5's E2-2 row 23, D-324 ruling 15) the door follows the matched customer's `merged_into_customer_id` one hop, so it never answers a merged customer: a channel whose customer was merged answers the winner, with the winner's status. One hop is always enough, because a later merge re-points every older tombstone at the new winner (§3.13).",
  request: jsonBody(ResolveIdentityRequest),
  responses: {
    ...ok("The owning customer + contact", ResolveIdentityResponse),
    403: problemResponse(
      "PERMISSION_DENIED — with no grant named when the caller's lineage is outside sales, facility_admin and group_admin; then `missing_permission: messaging.address.read` when the caller does not hold that grant (D-307), before the value is normalized or looked up, so a matching, a non-matching and a malformed value are refused alike",
    ),
    404: problemResponse("IDENTITY_NOT_FOUND"),
    422: problemResponse("IDENTITY_INVALID"),
  },
});

// ===========================================================================
// distributor eligible list (spec 09 §2.5; §5 rows 10–11)
// ===========================================================================

route({
  method: "post",
  path: "/v1/customers/{id}/distributor-products",
  operationId: "addSalesDistributorProduct",
  summary: "Add an eligible product (distributor rates)",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §2.5/§3.1.3 — only meaningful for tier='distributor'; other tiers answer 422 NOT_DISTRIBUTOR. The §3.2 engine resolves distributor rates ONLY for products on this list (otherwise b2b fallback, §9.5). Since P3-E2 PR E2-2b (spec 09 §5's E2-2 row 22b, §3.1 item 4, D-334 ruling 2, which answered §12 question 15) the add holds its customer FOR KEY SHARE first and refuses a MERGED customer 422 `CUSTOMER_ARCHIVED` with `extensions.merged_into_customer_id`, before NOT_DISTRIBUTOR, so a row can never land on a tombstone. A customer archived without a merge passes as before, because an admin may prepare a distributor list ahead of a restore.",
  request: { params: IdParam, ...jsonBody(DistributorProductAddRequest) },
  responses: {
    ...created("The eligible-list row", CustomerDistributorProduct),
    404: problemResponse("CUSTOMER_NOT_FOUND / PRODUCT_NOT_FOUND"),
    409: problemResponse("Already on the list (UNIQUE tenant/customer/product)"),
    422: problemResponse(
      `CUSTOMER_ARCHIVED, for a merged customer alone (§5's E2-2 row 22b); then NOT_DISTRIBUTOR. ${MERGED_EXTENSION}`,
    ),
  },
});

route({
  method: "delete",
  path: "/v1/customers/{id}/distributor-products/{productId}",
  operationId: "removeSalesDistributorProduct",
  summary: "Remove an eligible product",
  permission: "sales.tier.manage",
  request: { params: z.object({ id: z.guid(), productId: z.guid() }) },
  responses: {
    ...ok("Removal confirmation", DistributorProductRemoveResponse),
    404: problemResponse("CUSTOMER_NOT_FOUND / DISTRIBUTOR_PRODUCT_NOT_FOUND"),
  },
});

// ===========================================================================
// reliability (spec 09 §2.6, §3.9 — provisional; §5 row 12)
// ===========================================================================

route({
  method: "get",
  path: "/v1/customers/{id}/reliability",
  operationId: "getSalesCustomerReliability",
  summary: "Reliability snapshot history (provisional in P3)",
  permission: "sales.read",
  description:
    "Spec 09 §3.9 — the §2.6 snapshots with formula inputs, newest first. UNTIL MODULE 11 (P4) there is no invoice/payment history: the nightly job emits nothing, so the only rows are the P3-A seed's authored demo snapshots (the stated M09-S12 stand-in) and every row carries `provisional: true`. Formula inputs are omitted per §7 outside ga/fa/finance/sales-assigned; the score renders for every `sales.read` holder (it is the §4.1 chip).",
  request: { params: IdParam },
  responses: {
    ...ok(
      "Snapshots, newest first (empty for unscored customers)",
      CustomerReliabilityHistoryResponse,
    ),
    404: problemResponse("CUSTOMER_NOT_FOUND"),
  },
});

// ===========================================================================
// price lists (spec 09 §2.7–§2.8, D-234; §5 rows 14–18 + the detail read)
// ===========================================================================

route({
  method: "get",
  path: "/v1/price-lists",
  operationId: "listSalesPriceLists",
  summary: "List price lists (cursor page)",
  permission: "sales.read",
  request: {
    query: z.object({
      tier: CustomerTier.optional(),
      currency: SalesCurrency.optional(),
      status: PriceListStatus.optional(),
      ...cursorQuery,
    }),
  },
  responses: ok("Price lists, newest first", PriceListListResponse),
});

route({
  method: "get",
  path: "/v1/price-lists/{id}",
  operationId: "getSalesPriceList",
  summary: "Price list head + items (the §4.3 grid read)",
  permission: "sales.read",
  description:
    "Head + the full item set (a price list is a bounded definition set, unpaginated like the module-05/07 catalogs). `margin_preview_pct` (§4.3, price vs current standard cost) is present per item ONLY for readers who hold `sales.margin.read` AND `sales.read_value` (§7 omission style; margin requires value, D-301). This read is not a verbatim §5 row: §4.3's editable grid needs it and §5 omitted it — added as the table's necessary read, logged in the P3-A decision candidates. The item prices ARE the money §7 withholds (D-301): an order or quote line serves `price_source_ref`, the id of the item that priced it, to every reader, and the item id picks the quantity break, so this read would hand a reader without `sales.read_value` the exact total the line read omits. A caller without the grant is therefore answered 403 PERMISSION_DENIED with `missing_permission: sales.read_value`, before the lookup, so the refusal is the same for an id that does not exist. The list read (`GET /v1/price-lists`) serves heads without prices and stays open to every `sales.read` holder.",
  request: { params: IdParam },
  responses: {
    ...ok("The list with items", PriceListDetailResponse),
    403: problemResponse(
      "PERMISSION_DENIED — `missing_permission: sales.read_value` when the caller does not hold the grant (D-301), checked before the lookup",
    ),
    404: problemResponse("PRICE_LIST_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/price-lists",
  operationId: "createSalesPriceList",
  summary: "Create a draft price list",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §2.7 — status `draft`. `vat_mode` defaults BY TIER when omitted (D-234: market → inclusive, b2b/distributor → exclusive; a regional-convention default the owner flips as data by setting it explicitly).",
  request: jsonBody(PriceListCreateRequest),
  responses: {
    ...created("The draft list", PriceList),
    409: problemResponse("PRICE_LIST_CODE_TAKEN"),
  },
});

route({
  method: "put",
  path: "/v1/price-lists/{id}/items",
  operationId: "putSalesPriceListItems",
  summary: "Bulk upsert items (drafts only)",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §5 — replaces the draft's item set wholesale (rows absent from the payload are deleted); the row-validation report rejects bad rows with structured codes (D-225), same UX lane as module 02's imports. Active lists refuse edits (409 PRICE_LIST_NOT_DRAFT): repricing is a NEW draft list, which is what §2.7's validity ranges exist for.",
  request: { params: IdParam, ...jsonBody(PriceListItemsPutRequest) },
  responses: {
    ...ok("The row-validation report", PriceListItemsPutResponse),
    404: problemResponse("PRICE_LIST_NOT_FOUND"),
    409: problemResponse("PRICE_LIST_NOT_DRAFT"),
  },
});

route({
  method: "post",
  path: "/v1/price-lists/{id}/activate",
  operationId: "activateSalesPriceList",
  summary: "Activate a draft list",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §2.7 — group_admin-gated service-side until module 12's `price_activation_requires_approval` routing lands in P4 (D-231, §5). Runs the (tenant, tier, currency) active-validity overlap check; a conflict answers 409 VALIDITY_OVERLAP with `conflicting_price_list_id` in the problem extensions (§4.3's inline diff reads it).",
  request: { params: IdParam },
  responses: {
    ...ok("The activated list", PriceList),
    404: problemResponse("PRICE_LIST_NOT_FOUND"),
    409: problemResponse(
      "VALIDITY_OVERLAP (extension `conflicting_price_list_id`) / PRICE_LIST_NOT_DRAFT",
    ),
  },
});

route({
  method: "post",
  path: "/v1/price-lists/{id}/archive",
  operationId: "archiveSalesPriceList",
  summary: "Archive a list",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §5 — group_admin-gated service-side. Sent quotes keep their pinned prices (§9.3); drafts still pricing against the archived list reprice on next edit.",
  request: { params: IdParam },
  responses: {
    ...ok("The archived list", PriceList),
    404: problemResponse("PRICE_LIST_NOT_FOUND"),
  },
});

// ===========================================================================
// off-cut discount classes (spec 09 §2.10, D-229/D-209; §5 row 19)
// ===========================================================================

route({
  method: "get",
  path: "/v1/offcut-discount-classes",
  operationId: "listSalesOffcutClasses",
  summary: "List off-cut discount classes",
  permission: "sales.read",
  description:
    "A bounded definition set — unpaginated (the module-05/07 catalog rule). `name_i18n` renders in the reader's locale via localeText; the wire keeps `name` (D-209). Every `sales.read` holder reads the classes, because the composer names a line's class. `discount_pct` is served only to a caller holding `sales.read_value` and is absent for any other reader (D-337 ruling 6): a quote line serves its `offcut_class_id` to every reader, and the rate beside the product's list price would rebuild the line's price.",
  request: { query: z.object({ status: z.enum(["active", "archived"]).optional() }) },
  responses: ok("The classes", OffcutDiscountClassListResponse),
});

route({
  method: "post",
  path: "/v1/offcut-discount-classes",
  operationId: "createSalesOffcutClass",
  summary: "Create a discount class",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §2.10 — group_admin-gated service-side (§7 'Margin guard / off-cut class edit' is ga-alone). The vertical pack seeds OFFCUT_SINGLE (1 piece, 20%) and OFFCUT_COMBO2 (2 pieces, 30%) with all four locales authored. The answer carries `discount_pct` only for a caller holding `sales.read_value` (D-337 ruling 6). The service admits the literal group_admin alone, who holds the value grant, so no caller today is answered without the rate. The omission holds for any caller that would be.",
  request: jsonBody(OffcutDiscountClassCreateRequest),
  responses: {
    ...created("The created class", OffcutDiscountClass),
    409: problemResponse("OFFCUT_CLASS_CODE_TAKEN"),
  },
});

route({
  method: "patch",
  path: "/v1/offcut-discount-classes/{id}",
  operationId: "updateSalesOffcutClass",
  summary: "Edit or archive a discount class",
  permission: "sales.tier.manage",
  description:
    "Group_admin-gated service-side. Pinned sent-quote prices are unaffected by edits (§9.3's rule applies to every pricing input). The answer carries `discount_pct` only for a caller holding `sales.read_value` (D-337 ruling 6), the rule the create keeps.",
  request: { params: IdParam, ...jsonBody(OffcutDiscountClassUpdateRequest) },
  responses: {
    ...ok("The updated class", OffcutDiscountClass),
    404: problemResponse("OFFCUT_CLASS_NOT_FOUND"),
  },
});

// ===========================================================================
// margin guards (spec 09 §2.11; §5 row 20)
// ===========================================================================

route({
  method: "get",
  path: "/v1/margin-guards",
  operationId: "listSalesMarginGuards",
  summary: "List margin guards",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §5 — the guard editor's read (§4.3). Carries the governance code, not `sales.read`: a guard's `min_margin_pct` IS margin intelligence, and §5's role cell for the whole row group is group_admin (service-narrowed on mutations; fa reads under the same code).",
  request: {
    query: z.object({
      scope: MarginGuardScope.optional(),
      status: z.enum(["active", "archived"]).optional(),
      product_id: z.guid().optional(),
    }),
  },
  responses: ok("The guards (bounded set, unpaginated)", MarginGuardListResponse),
});

route({
  method: "post",
  path: "/v1/margin-guards",
  operationId: "createSalesMarginGuard",
  summary: "Create a margin guard",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §2.11 — group_admin-gated service-side. `cost_basis='moving_avg'` → 422 COST_BASIS_UNAVAILABLE until module 11's cost ledger (P4, stated degradation). `scope='family'` → 422 FAMILY_SCOPE_UNAVAILABLE: module 01's shipped schema has no product_families table, so the §2.11 family scope is undeclarable today (P3-A decision; the column ships FK-less for when 01 grows families). At most one ACTIVE guard per scope target (§2.11 partial uniques) — a duplicate answers 409 MARGIN_GUARD_EXISTS.",
  request: jsonBody(MarginGuardCreateRequest),
  responses: {
    ...created("The created guard", MarginGuard),
    409: problemResponse("MARGIN_GUARD_EXISTS"),
    422: problemResponse("COST_BASIS_UNAVAILABLE / FAMILY_SCOPE_UNAVAILABLE / PRODUCT_NOT_FOUND"),
  },
});

route({
  method: "patch",
  path: "/v1/margin-guards/{id}",
  operationId: "updateSalesMarginGuard",
  summary: "Edit or archive a margin guard",
  permission: "sales.tier.manage",
  request: { params: IdParam, ...jsonBody(MarginGuardUpdateRequest) },
  responses: {
    ...ok("The updated guard", MarginGuard),
    404: problemResponse("MARGIN_GUARD_NOT_FOUND"),
    409: problemResponse("MARGIN_GUARD_EXISTS — un-archiving into an occupied scope target"),
    422: problemResponse("COST_BASIS_UNAVAILABLE"),
  },
});

// ===========================================================================
// sales settings (spec 09 §2.22; §5 row 39)
// ===========================================================================

route({
  method: "get",
  path: "/v1/sales-settings",
  operationId: "getSalesSettings",
  summary: "Tenant sales settings",
  permission: "sales.read",
  description:
    "Spec 09 §2.22 — the singleton always exists (0050 backfill + ensure-on-first-touch). The reminder cadences, quiet hours and `delivery_auto_confirm_days` are read by the sales engine (P3-E PR-E2, D-292); the price-visibility toggles by the tracking page and the delivery-note renderer. Two READ-ONLY stamps ride the row since PR-E2 (migration 0067): `engine_swept_at`, when the engine last finished a sweep of this tenant (the settings screen's liveness line), and `reminders_active_since`, the activation horizon the tenant's first sweep stamps before computing any reminder — the reminder sequences chase only documents whose anchor is at or after it (D-292 ruling 5). Both are NULL until the first sweep, and the PUT neither accepts nor changes them.",
  responses: ok("The settings row", SalesSettings),
});

route({
  method: "put",
  path: "/v1/sales-settings",
  operationId: "putSalesSettings",
  summary: "Replace tenant sales settings (full row)",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §5 — facility_admin/group_admin (the code's role set). Full-row payload, no partial merge; `reminder_tier_overrides` keys are zod-validated (an unknown key answers 400 VALIDATION_FAILED, as does either engine stamp `engine_swept_at` / `reminders_active_since` sent on the PUT — the body is strict; §2.22). The three document bundles are optional (absent keeps the stored bundle, `{}` clears it), and their text is checked against the bundled document fonts on save: a character no font can draw answers 422 DOCUMENT_GLYPH_COVERAGE with `extensions.code_points`, because once stored it would stop every quote send in that language and every delivery note (D-288).",
  request: jsonBody(SalesSettingsPutRequest),
  responses: {
    ...ok("The updated settings row", SalesSettings),
    422: problemResponse(
      "DOCUMENT_GLYPH_COVERAGE — document text the bundled fonts cannot draw; `extensions.code_points`",
    ),
  },
});

// ===========================================================================
// pricing resolve (spec 09 §3.2; §5 row 25)
// ===========================================================================

route({
  method: "post",
  path: "/v1/pricing/resolve",
  operationId: "resolveSalesPricing",
  summary: "Price engine dry run (composer + agent)",
  permission: "sales.read",
  description:
    "Spec 09 §3.2 — tier list (with the distributor eligible-list fallback to b2b, surfaced as `distributor_fallback: true`, §9.5 — never an error) → customer override → off-cut class (from `fulfillment_hint`) → cost + margin → guard. Margin/cost fields are OMITTED without `sales.margin.read` AND `sales.read_value` (§7; margin requires value, D-301); `margin_breached` always answers. `unit_cost_minor` is the module-01 standard cost snapshot — the moving-average basis is module 11's (§2.11). No list row for the customer's (tier, currency, date, qty) → 422 PRICE_NOT_LISTED (§9.13: no silent FX, ever). A dry run: writes nothing, reserves nothing (§3.3 — reservation happens at acceptance). The answer is prices (`list_price_minor` and `unit_price_minor` are required), so a caller without `sales.read_value` is answered 403 PERMISSION_DENIED with `missing_permission: sales.read_value` before any query (D-301); the service then narrows to the sales, facility_admin and group_admin lineages, and refuses the rest 403 with no grant named.",
  request: jsonBody(PricingResolveRequest),
  responses: {
    ...ok("The §3.2 resolution", PricingResolveResponse),
    403: problemResponse(
      "PERMISSION_DENIED — `missing_permission: sales.read_value` when the caller does not hold the grant (D-301), before any query; with no grant named when the caller's lineage is outside sales, facility_admin and group_admin",
    ),
    404: problemResponse("CUSTOMER_NOT_FOUND / PRODUCT_NOT_FOUND / UOM_NOT_FOUND"),
    422: problemResponse(`PRICE_NOT_LISTED / CUSTOMER_ARCHIVED. ${MERGED_EXTENSION}`),
  },
});

// ===========================================================================
// customer price overrides (spec 09 §2.9, §3.2 step 2, §3.4, §4.2 Pricing tab,
// §4.6; §5's four override rows) — SLICE P3-C-2 (2026-09-08, D-263).
//
// The §2.9 table has existed since P3-A (0050; `min_qty` since 0055, D-241)
// with NO writer in the product: the §3.2 engine read it, the §4.6 feed carded
// its pending rows, and nothing could create one. These four routes are that
// writer. The review seam is D-231's in-module shape: the row carries its own
// `pending_approval` state, its own decide route under `sales.margin.approve`,
// and its own decided event (`sales.override_review_decided`, §6) — module 12
// absorbs the surface in P4 additively, nothing here is renamed.
// ===========================================================================

route({
  method: "post",
  path: "/v1/customer-price-overrides",
  operationId: "createSalesCustomerPriceOverride",
  summary: "Propose a negotiated price for one customer × product (§2.9)",
  permission: "sales.override.write",
  description:
    "Spec 09 §2.9 / §3.4 / §7 ('auto-routes to approval on breach'). The body is a discriminated union on `override_kind` — `fixed_price` carries `unit_price_minor` (per the product's BASE UoM, D-237), `discount_pct` carries the percentage applied to the resolved step-1 tier price — so the §2.9 kind-pairing CHECK is the request's shape, never a refusal. `currency` is NOT a body field: it is stamped from the customer's `default_currency`, because the §3.2 engine resolves an override only when its currency matches the document's (§9.13 — no silent FX) and any other value would author a row that never prices. `min_qty` is the D-241 quantity scale in the base UoM (default `\"0\"` — applies at every quantity; greatest qualifying wins, as §2.8's break does). THE GUARD RUNS AT SUBMIT: the service evaluates the override through the §3.2 engine in the frame `CustomerPriceOverrideReview` states (the smallest quantity it applies at, on its first effective day). Inside the guard → 201 with status `active`, and the next `POST /v1/pricing/resolve` for this customer × product answers `price_source: customer_override`. Breaching, or UNPROVABLE (no released standard cost, no UoM conversion path, cross-currency cost — the guard fails closed, §2.9/§2.14) → 201 with status `pending_approval`, `review` carrying the breach evidence (§7-redacted by omission), a `price_override` card in the §4.6 inbox, and the engine IGNORES the row until it is decided (§3.2 step 2). The sales role proposes only for its ASSIGNED customers (service-narrowed on this code, D-040 two-layer); facility_admin and group_admin for any. Audited; the agent's §8 `propose_customer_price_override` (ask) lands on this route with P3-K.",
  request: jsonBody(CustomerPriceOverrideCreateRequest),
  responses: {
    ...created(
      "The override — `active`, or `pending_approval` with its `review` evidence",
      CustomerPriceOverride,
    ),
    404: problemResponse("CUSTOMER_NOT_FOUND / PRODUCT_NOT_FOUND"),
    422: problemResponse(
      `CUSTOMER_ARCHIVED — archived customers block new documents (§3.1.4); PRICE_NOT_LISTED — an override of EITHER kind (\`fixed_price\` as well as \`discount_pct\`) for a (customer, product) with no active list price on the customer's tier at \`valid_from\`, refused rather than stored dead: §3.2 only reaches step 2 after step 1 found a list row, so the row would sit \`active\` and never price. ${MERGED_EXTENSION}`,
    ),
  },
});

route({
  method: "post",
  path: "/v1/customer-price-overrides/{id}/decide",
  operationId: "decideSalesCustomerPriceOverride",
  summary: "Decide a `pending_approval` override (D-231)",
  permission: "sales.margin.approve",
  description:
    "Spec 09 §5 / §3.4 / D-231 — the in-module review decision for a price override, the same shape as `POST /v1/quotes/{id}/decide`. `approved` → status `active`, and the §3.2 engine prices from the row on its next resolve. `rejected` → status `revoked` with the MANDATORY note stored as the row's `reason` (§5, §10 M09-S16) — the proposer's original justification stays in the audit chain and in the event, not on the row. Either way the row leaves the §4.6 inbox and `sales.override_review_decided` is emitted (§6) with the outcome, the decider and the note. THE §3.4 THRESHOLD IS ENFORCED HERE, not in the client: the service RE-EVALUATES the guard against the current cost and list (§2.9 stores no verdict — the D-255 amendment's rule), and a facility_admin may decide a breach within 2 percentage points; above it, or where the margin cannot be proved, the decision is group_admin's and a facility_admin answers 403 APPROVAL_THRESHOLD_EXCEEDED. The §4.6 card's `decidable_by_caller` is computed from the same rule so the screen never offers a press that will be refused.",
  request: { params: IdParam, ...jsonBody(CustomerPriceOverrideDecideRequest) },
  responses: {
    ...ok("The decided override (`active` or `revoked`)", CustomerPriceOverride),
    403: problemResponse("APPROVAL_THRESHOLD_EXCEEDED — facility_admin above the §3.4 threshold"),
    404: problemResponse("OVERRIDE_NOT_FOUND (cross-tenant ids are invisible)"),
    409: problemResponse("OVERRIDE_NOT_PENDING — the row is not in `pending_approval`"),
    422: problemResponse("DECISION_NOTE_REQUIRED — reject without a note"),
  },
});

route({
  method: "get",
  path: "/v1/customers/{id}/price-overrides",
  operationId: "listSalesCustomerPriceOverrides",
  summary: "A customer's price overrides (the §4.2 Pricing tab)",
  permission: "sales.read",
  description:
    "Spec 09 §4.2 / §5 — every §2.9 row for the customer with its validity and approval state, newest first (`created_at` desc, id desc; the cursor encodes that order). Filters: `status` (one of the §2.9 states) and `product_id`. The Pricing tab is the one reader: the §3.2 engine reads the table directly, and the §4.6 inbox reads only the pending rows through its own feed. `unit_price_minor` and `discount_pct` are OMITTED — both keys, whatever the kind — for a reader without `sales.read_value` (§7's field grant; D-225: omission, never null); `review` is present on `pending_approval` rows only, itself redacted per §7. The sales role sees only its ASSIGNED customers' rows (the §7 scope note, service-enforced on top of RLS); a customer outside the assignment answers 404 CUSTOMER_NOT_FOUND, never a different error for 'exists but not yours' (§9.15's row-or-nothing rule).",
  request: {
    params: IdParam,
    query: z.object({
      status: OverrideStatus.optional(),
      product_id: z.guid().optional(),
      ...cursorQuery,
    }),
  },
  responses: {
    ...ok("Overrides, newest first", CustomerPriceOverrideListResponse),
    404: problemResponse("CUSTOMER_NOT_FOUND"),
  },
});

route({
  method: "post",
  path: "/v1/customer-price-overrides/{id}/revoke",
  operationId: "revokeSalesCustomerPriceOverride",
  summary: "Revoke an override (facility_admin / group_admin)",
  permission: "sales.override.write",
  description:
    "Spec 09 §5 — `status → revoked`, audited, with a mandatory reason. THE ROLE IS NARROWER THAN THE CODE (D-263; D-040 two-layer, the `sales.tier.manage` ga-only-cells shape): the code is held by ga/fa/sales because ONE code gates create and revoke, and revoke is narrowed service-side to facility_admin and group_admin — a sales user holding the code answers 403 PERMISSION_DENIED naming the role (§10 M09-S16's last line). Legal FROM `active` (the negotiated rate is withdrawn) and FROM `pending_approval` (an admin withdraws a proposal before it is decided — no event: nothing was decided); `expired` and `revoked` answer 409 OVERRIDE_TERMINAL, because revoking a window that already closed rewrites history and revoking twice would silently discard the second reason. Effects follow §9.3: a SENT quote's pinned price is unchanged; a draft still pricing from this row reprices from the tier list on its next edit.",
  request: { params: IdParam, ...jsonBody(CustomerPriceOverrideRevokeRequest) },
  responses: {
    ...ok("The revoked override", CustomerPriceOverride),
    403: problemResponse(
      "PERMISSION_DENIED — revoke is facility_admin / group_admin; the sales role holds the code for create only",
    ),
    404: problemResponse("OVERRIDE_NOT_FOUND"),
    409: problemResponse("OVERRIDE_TERMINAL — already `expired` or `revoked`"),
  },
});

// ===========================================================================
// sales activities — the manager's TASKS (spec 09 §5; P3-E PR-E2, D-292
// rulings 1–4; migration 0067)
//
// ACTIVITIES STEP 1: a list, a manual create and a complete. The engine
// writes the SYSTEM tasks (a reminder sequence that ended with the customer
// still not acting, a message that could not be sent for want of a channel)
// and closes them itself when the state they chase ends; there is NO cancel
// route, and a manual task never closes itself. ONE new code,
// `sales.activity.write` ({group_admin, facility_admin, sales} — the role set
// of `sales.quote.write`, but a different policy surface, D-074's test as
// D-283 ruling 1 applies it). The list rides `sales.read`, narrowed
// service-side (D-040 two-layer).
//
// P3-E2 PR E2-1 (D-313) adds the rest: the list gains `opportunity_id`,
// `assignee_user_id`, `due` and a typed `order` (declared default
// `worklist`), items gain the deal subject and `cancelled_by`, and each
// subject label follows its own door (D-312 ruling 21). Two new rows: one
// PATCH for a new day, a new assignee or new words (ruling 1), and WITHDRAW
// for a manual task (ruling 2) — no task is ever deleted. No new code: both
// ride `sales.activity.write`. Tasks still emit no event (ruling 11).
// ===========================================================================

const ActivityIdParam = z.object({ id: z.guid() });

route({
  method: "get",
  path: "/v1/sales-activities",
  operationId: "listSalesActivities",
  summary: "The task list (cursor page)",
  permission: "sales.read",
  description:
    "Spec 09 §5 (P3-E PR-E2, D-292 ruling 2; P3-E2 PR E2-1, D-313). `scope=mine` (the default) lists the tasks assigned to the caller; `scope=all` lists the caller's whole visible set — never wider than the narrowing below, and never a 403 for a non-admin (D-224 ruling 5: a filter is intersected, never widened). NARROWING (service-side, D-040; §7 narrowing 10): a caller holding the literal group_admin or facility_admin role reads every task; a sales principal reads the tasks assigned to them and the tasks on customers they are assigned to; any other lineage reads only its own. A document's open-task line asks `scope=all` with its `sales_order_id`, `quote_id` or `opportunity_id`, so each reader sees every task on the document they may see. `status` defaults to `open`. ORDER: `order` takes `worklist` (the declared default, the order PR-E2 shipped byte for byte: open tasks by `due_on` then `created_at` ascending, done and cancelled tasks by `updated_at` newest first, ties by id), `due_on`, `-due_on` or `-updated_at` (D-313 ruling 4); the cursor is an exact keyset over the effective order at full timestamp precision, and a cursor presented under another order answers 400 `invalid_cursor`. Filters AND together: `customer_id`, `quote_id`, `sales_order_id`, `opportunity_id`, `assignee_user_id` (applied inside the narrowing, so it only narrows) and `due` (`overdue`, `today`, `through_today` or `upcoming` — after today and within seven days — each against the tenant's business today, D-313 ruling 3). An id that is not a uuid or a value outside its enum answers 400 VALIDATION_FAILED, never an empty page, and a filter naming a document or deal the caller cannot read answers an empty page, never a 404 that confirms it exists. `overdue` is server-computed against the tenant's business today. Each subject label (`customer_name`, `quote_no`, `order_no`, `opportunity_no`, `opportunity_title`) is null when its id is, and also when that record's own door would not answer this reader, while the id is still served (D-312 ruling 21). A withdrawn task reads `cancelled` with `cancel_reason: WITHDRAWN` and `cancelled_by`. A system task carries no prose: `origin` + `reason_code` + its subject are the message (D-225).",
  request: { query: SalesActivityListQuery },
  responses: {
    ...ok("Tasks, in the order the status asks for", SalesActivityListResponse),
    400: problemResponse(
      "VALIDATION_FAILED — a filter outside its enum, an id that is not a uuid, or a malformed cursor",
    ),
  },
});

route({
  method: "post",
  path: "/v1/sales-activities",
  operationId: "createSalesActivity",
  summary: "Create a manual task",
  permission: "sales.activity.write",
  description:
    "Spec 09 §5 (P3-E PR-E2, D-292 ruling 2; the deal subject since P3-E2 PR E2-1, D-313 ruling 5). A MANUAL task: `origin: manual`, `status: open`, the caller as `created_by`. `assignee_user_id` absent means the caller. A caller without the literal group_admin or facility_admin role creates only for themselves — naming anyone else answers 422 ACTIVITY_ASSIGNEE_NOT_ELIGIBLE; such an admin may assign an ACTIVE member of the tenant who holds `sales.activity.write` and can read the subject (a sales assignee: an assigned customer, a document they own, or a deal they read), and anyone else answers the same 422. This text said `sales.read` until E2-1, while the service checked `sales.activity.write` throughout; D-313 ruling 10 corrects the published text, and the door is unchanged. Each subject (`customer_id`, `quote_id`, `sales_order_id`, `opportunity_id`) is optional, and a named subject must be one the caller can read: an invisible one answers its own not-found (CUSTOMER_NOT_FOUND / QUOTE_NOT_FOUND / ORDER_NOT_FOUND / OPPORTUNITY_NOT_FOUND), never a different error for 'exists but not yours'. The subjects agree in the order customer, quote, order, deal: the customer is derived from the quote, the order or the deal when `customer_id` is absent, and a subject of another customer, an order that is not the named quote's, or a quote linked to another deal answers 400 VALIDATION_FAILED with `errors[0].code: subject_mismatch` (path `quote_id` or `sales_order_id` for the quote and order checks, `opportunity_id` for the deal). The assignee is checked first: an ineligible assignee answers 422 before any subject is resolved. `due_on` is a tenant business date and may be in the past (the task is then overdue at once). Audited `sales_activity.created`. `Idempotency-Key` is required.",
  request: jsonBody(SalesActivityCreateRequest),
  responses: {
    ...created("The created task", SalesActivity),
    400: problemResponse(
      "VALIDATION_FAILED — a malformed body, or `subject_mismatch`: the named subjects belong to different customers",
    ),
    404: problemResponse(
      "CUSTOMER_NOT_FOUND / QUOTE_NOT_FOUND / ORDER_NOT_FOUND / OPPORTUNITY_NOT_FOUND",
    ),
    422: problemResponse(
      `ACTIVITY_ASSIGNEE_NOT_ELIGIBLE — a non-admin naming another assignee, or an assignee who is not an active member holding \`sales.activity.write\` who can read the subject; then, last, CUSTOMER_ARCHIVED for a MERGED customer alone: the create holds the task's customer FOR KEY SHARE and refuses a tombstone, while a customer archived without a merge still takes a task, because a win-back reminder is real work (spec 09 §5's E2-2 row 22b, D-334 ruling 2). A task that names only a quote, an order or a deal follows its document to the customer it now sits on. ${MERGED_EXTENSION}`,
    ),
  },
});

route({
  method: "post",
  path: "/v1/sales-activities/{id}/complete",
  operationId: "completeSalesActivity",
  summary: "Complete a task",
  permission: "sales.activity.write",
  description:
    "Spec 09 §5 (P3-E PR-E2, D-292 ruling 2). `status: open → done`, stamping `done_at` and `done_by` (the caller). The ASSIGNEE or an admin (group_admin, facility_admin) completes a task — system tasks included, which is how a manager closes the loop after telling the customer by other means. Refusals: 404 ACTIVITY_NOT_FOUND for a task that does not exist, belongs to another tenant, or lies outside the caller's read narrowing (the list's own rule, so the door never confirms a task the caller cannot see); 403 PERMISSION_DENIED for a reader who can see the task but is neither its assignee nor an admin; 409 ACTIVITY_NOT_OPEN for a task already `done`, closed by the engine (`cancelled`, `PREMISE_ENDED`) or withdrawn (`cancelled`, `WITHDRAWN`). Audited. `Idempotency-Key` is required.",
  request: { params: ActivityIdParam },
  responses: {
    ...ok("The completed task", SalesActivity),
    403: problemResponse(
      "PERMISSION_DENIED — the caller can read the task but is neither its assignee nor an admin",
    ),
    404: problemResponse("ACTIVITY_NOT_FOUND"),
    409: problemResponse(
      "ACTIVITY_NOT_OPEN — the task is already done, withdrawn, or closed by the engine",
    ),
  },
});

route({
  method: "patch",
  path: "/v1/sales-activities/{id}",
  operationId: "updateSalesActivity",
  summary: "Move a task to another day, reassign it, or edit its words",
  permission: "sales.activity.write",
  description:
    "Spec 09 §3.12 and §5 (P3-E2 PR E2-1, D-313 ruling 1). One request and one audit row, `sales_activity.updated`, naming the fields that changed with `from` and `to` only for `due_on` and `assignee_user_id`, so a task's title and note never reach the audit chain. At least one key. `due_on` (the assignee or a literal admin; a past date makes the task overdue at once); `assignee_user_id` (a caller holding the literal group_admin or facility_admin role only, onto an active member holding `sales.activity.write` who can read the subject — someone who could have been named at create); `kind`, `title` and `note` (a MANUAL task's words only; a system task's words come from its reason and never change, and `note: null` clears the note). §7 narrowing 11 as extended. No event. The door refuses, in this order: 404 ACTIVITY_NOT_FOUND (the list's predicate, so the door never confirms a task the caller cannot see), 403 PERMISSION_DENIED for a caller who can see the task but is neither its assignee nor a literal admin, 409 ACTIVITY_NOT_OPEN, 400 VALIDATION_FAILED with the reason `system_task_words` when `kind`, `title` or `note` is sent for a system task, and 422 ACTIVITY_ASSIGNEE_NOT_ELIGIBLE for an assignee the caller may not name (the create's rule: a non-admin naming anyone but themselves, or an admin naming an ineligible member). `Idempotency-Key` is required.",
  request: { params: ActivityIdParam, ...jsonBody(SalesActivityUpdateRequest) },
  responses: {
    ...ok("The task as it now stands", SalesActivity),
    400: problemResponse(
      "VALIDATION_FAILED — a malformed body or no key; or, after the row checks, `system_task_words`: `kind`, `title` or `note` sent for a system task",
    ),
    403: problemResponse(
      "PERMISSION_DENIED — the caller can read the task but is neither its assignee nor a literal admin",
    ),
    404: problemResponse("ACTIVITY_NOT_FOUND"),
    409: problemResponse(
      "ACTIVITY_NOT_OPEN — the task is done, closed by the engine, or withdrawn",
    ),
    422: problemResponse(
      "ACTIVITY_ASSIGNEE_NOT_ELIGIBLE — a non-admin naming anyone but themselves, or a new assignee who is not an active member holding `sales.activity.write` who can read the subject",
    ),
  },
});

route({
  method: "post",
  path: "/v1/sales-activities/{id}/withdraw",
  operationId: "withdrawSalesActivity",
  summary: "Withdraw a manual task",
  permission: "sales.activity.write",
  description:
    "Spec 09 §3.12 and §5 (P3-E2 PR E2-1, D-313 ruling 2). Takes no body. `status: open → cancelled` with `cancel_reason: WITHDRAWN`, `cancelled_at` and `cancelled_by` (the caller). No task is ever deleted (D-241 ruling 2 kills the recycle bin), so a withdrawn task reads as withdrawn among the closed ones. A caller holding the literal group_admin or facility_admin role withdraws any manual task, and anyone else only a manual task assigned to them that they wrote themselves (`created_by = assignee_user_id`; §7 narrowing 17). A system task closes itself when its reason ends and is never withdrawn: it has no author, so its assignee meets 403 and only an admin meets 409 ACTIVITY_NOT_WITHDRAWABLE. Audited `sales_activity.withdrawn` (the assignee, never the words). No event. The door refuses, in this order: 404 ACTIVITY_NOT_FOUND, 403 PERMISSION_DENIED for a caller who is neither a literal admin nor the assignee who wrote the task, 409 ACTIVITY_NOT_WITHDRAWABLE for a system task, and 409 ACTIVITY_NOT_OPEN. `Idempotency-Key` is required.",
  request: { params: ActivityIdParam },
  responses: {
    ...ok("The withdrawn task", SalesActivity),
    403: problemResponse(
      "PERMISSION_DENIED — neither a literal admin nor the assignee who wrote the task",
    ),
    404: problemResponse("ACTIVITY_NOT_FOUND"),
    409: problemResponse(
      "ACTIVITY_NOT_WITHDRAWABLE — a system task / ACTIVITY_NOT_OPEN — the task is done, closed by the engine, or already withdrawn",
    ),
  },
});
